// src/features/quotation/service/quotationService.js
import {
  db,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  addDoc,
} from "../../../config/firebase";
import { QUOTATION_STATUS } from "../types/quotationTypes";
import {
  calculateTotals,
  generateQuotationNumber,
} from "../utils/quotationHelpers";

class QuotationService {
  // Collection reference
  getCollection() {
    return collection(db, "quotations");
  }

  // Get all quotations with optional filters
  async getQuotations(filters = {}) {
    try {
      let q = query(this.getCollection(), orderBy("createdAt", "desc"));

      // Apply filters
      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }

      if (filters.clientId && filters.clientId !== "all") {
        q = query(q, where("clientId", "==", filters.clientId));
      }

      const querySnapshot = await getDocs(q);
      const quotations = [];
      querySnapshot.forEach((doc) => {
        quotations.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return quotations;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get quotation by ID
  async getQuotationById(id) {
    try {
      const docRef = doc(db, "quotations", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        };
      } else {
        throw new Error("Quotation not found");
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Create new quotation
  async createQuotation(data) {
    try {
      // Get current count for generating number
      const countSnapshot = await getDocs(this.getCollection());
      const count = countSnapshot.size + 1;

      // Calculate totals
      const totals = calculateTotals(
        data.items,
        data.discount || 0,
        data.discountType || "percentage",
        data.taxRate || 0,
      );

      const quotationData = {
        ...data,
        ...totals,
        number: generateQuotationNumber(count),
        status: QUOTATION_STATUS.DRAFT,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        statusHistory: [
          {
            status: QUOTATION_STATUS.DRAFT,
            date: new Date().toISOString(),
            note: "Quotation created",
          },
        ],
        isConverted: false,
        invoiceId: null,
        activities: [
          {
            type: "created",
            date: new Date().toISOString(),
            note: "Quotation created",
          },
        ],
      };

      const docRef = await addDoc(this.getCollection(), quotationData);
      const newDoc = await getDoc(docRef);

      return {
        id: newDoc.id,
        ...newDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update quotation
  async updateQuotation(id, data) {
    try {
      const docRef = doc(db, "quotations", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Quotation not found");
      }

      let updateData = { ...data };

      // Recalculate totals if items changed
      if (data.items) {
        const totals = calculateTotals(
          data.items,
          data.discount || 0,
          data.discountType || "percentage",
          data.taxRate || 0,
        );
        updateData = { ...updateData, ...totals };
      }

      updateData.updatedAt = serverTimestamp();

      await updateDoc(docRef, updateData);

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete quotation
  async deleteQuotation(id) {
    try {
      const docRef = doc(db, "quotations", id);
      await deleteDoc(docRef);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update quotation status
  async updateStatus(id, status) {
    try {
      const docRef = doc(db, "quotations", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Quotation not found");
      }

      const currentData = docSnap.data();
      const statusHistory = currentData.statusHistory || [];

      statusHistory.push({
        status: status,
        date: new Date().toISOString(),
        note: `Status changed to ${status}`,
      });

      const updateData = {
        status: status,
        statusHistory: statusHistory,
        updatedAt: serverTimestamp(),
      };

      // Add specific timestamps based on status
      if (status === QUOTATION_STATUS.SENT) {
        updateData.sentAt = serverTimestamp();
      } else if (status === QUOTATION_STATUS.VIEWED) {
        updateData.viewedAt = serverTimestamp();
      } else if (status === QUOTATION_STATUS.APPROVED) {
        updateData.approvedAt = serverTimestamp();
      } else if (status === QUOTATION_STATUS.REJECTED) {
        updateData.rejectedAt = serverTimestamp();
      } else if (status === QUOTATION_STATUS.EXPIRED) {
        updateData.expiredAt = serverTimestamp();
      } else if (status === QUOTATION_STATUS.CONVERTED) {
        updateData.convertedAt = serverTimestamp();
      }

      await updateDoc(docRef, updateData);

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Send quotation to client
  async sendQuotation(id) {
    try {
      const docRef = doc(db, "quotations", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Quotation not found");
      }

      const currentData = docSnap.data();
      const statusHistory = currentData.statusHistory || [];

      statusHistory.push({
        status: QUOTATION_STATUS.SENT,
        date: new Date().toISOString(),
        note: "Quotation sent to client",
      });

      await updateDoc(docRef, {
        status: QUOTATION_STATUS.SENT,
        sentAt: serverTimestamp(),
        statusHistory: statusHistory,
        updatedAt: serverTimestamp(),
      });

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Convert quotation to invoice
  async convertToInvoice(id) {
    try {
      const docRef = doc(db, "quotations", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Quotation not found");
      }

      const currentData = docSnap.data();
      const statusHistory = currentData.statusHistory || [];

      statusHistory.push({
        status: QUOTATION_STATUS.CONVERTED,
        date: new Date().toISOString(),
        note: "Quotation converted to invoice",
      });

      // Here you would create an invoice in the invoices collection
      // For now, we just mark it as converted
      await updateDoc(docRef, {
        status: QUOTATION_STATUS.CONVERTED,
        isConverted: true,
        convertedAt: serverTimestamp(),
        statusHistory: statusHistory,
        updatedAt: serverTimestamp(),
      });

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get quotation statistics
  async getQuotationStats() {
    try {
      const querySnapshot = await getDocs(this.getCollection());
      const quotations = [];
      querySnapshot.forEach((doc) => {
        quotations.push(doc.data());
      });

      const total = quotations.length;
      const statusCounts = {
        draft: 0,
        sent: 0,
        viewed: 0,
        approved: 0,
        rejected: 0,
        expired: 0,
        converted: 0,
      };

      let totalValue = 0;
      let approvedCount = 0;
      let respondedCount = 0;

      quotations.forEach((q) => {
        if (statusCounts[q.status] !== undefined) {
          statusCounts[q.status]++;
        }
        totalValue += q.total || 0;
        if (q.status === "approved" || q.status === "rejected") {
          respondedCount++;
        }
        if (q.status === "approved") {
          approvedCount++;
        }
      });

      const conversionRate = total > 0 ? (approvedCount / total) * 100 : 0;
      const responseRate = total > 0 ? (respondedCount / total) * 100 : 0;
      const averageValue = total > 0 ? totalValue / total : 0;

      return {
        total,
        ...statusCounts,
        totalValue: Math.round(totalValue * 100) / 100,
        averageValue: Math.round(averageValue * 100) / 100,
        conversionRate: Math.round(conversionRate * 100) / 100,
        responseRate: Math.round(responseRate * 100) / 100,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Handle errors
  handleError(error) {
    console.error("Firebase error:", error);
    return new Error(error.message || "An error occurred");
  }
}

const quotationService = new QuotationService();
export default quotationService;
